/**
 * S3 — Version / blueprint editor
 * (`/ai-studio/agents/:agentId/versions/:versionId`), WF-F1 steps 4–8.
 *
 * Composes the blueprint tabs over `useVersionEditor`, which owns draft state and
 * persistence. Publishing is the point of no return: it makes the version bindable
 * by tenants and freezes it, so the action is confirmed in-page and the editor drops
 * to read-only afterwards.
 *
 * Tab panels stay mounted (hidden when inactive) so JSON editors keep local text and
 * validity across tab switches — unmounting would clear mid-edit invalid state and
 * leave Save incorrectly enabled/disabled.
 */

import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { LifecycleBadge } from '@/components/ai-studio/agents';
import {
  VersionGovernanceTab,
  VersionJsonTab,
  VersionPromptsTab,
  VersionStagesTab,
  useVersionEditor,
} from '@/components/ai-studio/versions';
import type { JsonBlueprintField } from '@/components/ai-studio/versions';
import { createPromptBinding } from '@/services/aiStudioAgentService';

type EditorTab = 'stages' | 'contracts' | 'engine' | 'governance' | 'prompts';

const TABS: readonly EditorTab[] = ['stages', 'contracts', 'engine', 'governance', 'prompts'];

const AIStudioVersionEditorPage: React.FC = () => {
  const { t } = useLanguage();
  const { agentId, versionId } = useParams<{ agentId: string; versionId: string }>();
  const editor = useVersionEditor(versionId);
  const [activeTab, setActiveTab] = useState<EditorTab>('stages');
  const [addingBinding, setAddingBinding] = useState(false);
  const [bindingErrorKey, setBindingErrorKey] = useState<string | null>(null);
  const [confirmingPublish, setConfirmingPublish] = useState(false);

  const { draft, version, readOnly } = editor;

  const handleAddBinding = useCallback(
    async (request: { promptRole: string; taskType: string }) => {
      if (!versionId) return;
      setAddingBinding(true);
      setBindingErrorKey(null);
      try {
        const binding = await createPromptBinding(versionId, request);
        editor.setPromptBindings([...editor.promptBindings, binding]);
      } catch {
        setBindingErrorKey('aiStudio.versions.prompts.addFailed');
      } finally {
        setAddingBinding(false);
      }
    },
    [editor, versionId]
  );

  const handleConfirmPublish = useCallback(async () => {
    await editor.publish();
    setConfirmingPublish(false);
  }, [editor]);

  const agentLink = agentId ? `/ai-studio/agents/${agentId}` : '/ai-studio/agents';

  if (editor.loading) {
    return (
      <main className="px-4 py-4 md:px-6 lg:px-8">
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.agents.loading')}
        </p>
      </main>
    );
  }

  if (!version || !draft) {
    return (
      <main className="flex flex-col gap-3 px-4 py-4 md:px-6 lg:px-8">
        <p role="alert" className="text-base text-[var(--color-error)]">
          {t(editor.errorKey ?? 'aiStudio.versions.notFound')}
        </p>
        <Link to={agentLink} className="w-fit text-base text-[var(--color-info)] underline">
          {t('aiStudio.versions.backToAgent')}
        </Link>
      </main>
    );
  }

  // Bumps when the persisted revision changes so JSON panels re-seed after save/publish.
  const documentKey = `${version.id}:${version.updatedAt}`;

  const jsonValues: Record<JsonBlueprintField, Record<string, unknown>> = {
    dataSchema: draft.dataSchema,
    outputSchema: draft.outputSchema,
    contextContract: draft.contextContract,
    engineConfig: draft.engineConfig,
    benchmarkConfig: draft.benchmarkConfig,
    opportunityLogic: draft.opportunityLogic,
    promotionRules: draft.promotionRules,
  };

  return (
    <main className="flex w-full flex-col gap-4 px-4 py-4 md:px-6 lg:px-8">
      <nav>
        <Link to={agentLink} className="text-base text-[var(--color-info)] underline">
          {t('aiStudio.versions.backToAgent')}
        </Link>
      </nav>

      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-3 text-lg font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.versions.title', { number: version.versionNumber })}
          <LifecycleBadge status={version.lifecycleStatus} />
        </h1>
        {!readOnly && !confirmingPublish && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void editor.save()}
              disabled={editor.saving || !editor.dirty || !editor.jsonValid}
              className="rounded-md border border-[var(--color-border)] px-4 py-2 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
            >
              {editor.saving ? t('aiStudio.common.saving') : t('aiStudio.common.save')}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingPublish(true)}
              disabled={editor.publishing || !editor.jsonValid}
              className="rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-50"
            >
              {t('aiStudio.versions.publish')}
            </button>
          </div>
        )}
      </header>

      {confirmingPublish && !readOnly && (
        <section
          role="region"
          aria-label={t('aiStudio.versions.publishConfirmTitle')}
          className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-4"
        >
          <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
            {t('aiStudio.versions.publishConfirmTitle')}
          </h2>
          <p className="mt-2 text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.versions.publishConfirm')}
          </p>
          {editor.dirty && (
            <p className="mt-2 text-base text-[var(--color-surface-text-secondary)]">
              {t('aiStudio.versions.publishWillSave')}
            </p>
          )}
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => void handleConfirmPublish()}
              disabled={editor.publishing || !editor.jsonValid}
              className="rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-50"
            >
              {editor.publishing
                ? t('aiStudio.versions.publishing')
                : t('aiStudio.versions.publishConfirmAction')}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingPublish(false)}
              disabled={editor.publishing}
              className="rounded-md border border-[var(--color-border)] px-4 py-2 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
            >
              {t('aiStudio.common.cancel')}
            </button>
          </div>
        </section>
      )}

      {readOnly && (
        <p className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] px-4 py-2 text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.versions.publishedNotice')}
        </p>
      )}

      {editor.errorKey && (
        <div
          role="alert"
          className="rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
        >
          {t(editor.errorKey)}
        </div>
      )}

      {!editor.jsonValid && !readOnly && (
        <p role="status" className="text-base text-[var(--color-error)]">
          {t('aiStudio.versions.fixInvalid')}
        </p>
      )}

      <div role="tablist" className="flex flex-wrap gap-1 border-b border-[var(--color-border)]">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] ${
              activeTab === tab
                ? 'border-b-2 border-[var(--color-accent-bg)] font-medium text-[var(--color-surface-text)]'
                : 'text-[var(--color-surface-text-secondary)] hover:text-[var(--color-surface-text)]'
            }`}
          >
            {t(`aiStudio.versions.tabs.${tab}`)}
          </button>
        ))}
      </div>

      <div className={activeTab === 'stages' ? '' : 'hidden'} role="tabpanel">
        <VersionStagesTab
          enabledStages={draft.enabledStages}
          industryPackId={draft.industryPackId}
          packs={editor.packs}
          disabled={readOnly}
          onChange={editor.applyPatch}
        />
      </div>

      <div className={activeTab === 'contracts' ? '' : 'hidden'} role="tabpanel">
        <VersionJsonTab
          panels={[
            { field: 'dataSchema', label: t('aiStudio.versions.dataSchema') },
            { field: 'outputSchema', label: t('aiStudio.versions.outputSchema') },
            { field: 'contextContract', label: t('aiStudio.versions.contextContract') },
          ]}
          values={jsonValues}
          documentKey={documentKey}
          disabled={readOnly}
          onChange={editor.applyPatch}
          onValidityChange={editor.setJsonValidity}
        />
      </div>

      <div className={activeTab === 'engine' ? '' : 'hidden'} role="tabpanel">
        <VersionJsonTab
          panels={[
            { field: 'engineConfig', label: t('aiStudio.versions.engineConfig') },
            { field: 'benchmarkConfig', label: t('aiStudio.versions.benchmarkConfig') },
            { field: 'opportunityLogic', label: t('aiStudio.versions.opportunityLogic') },
            { field: 'promotionRules', label: t('aiStudio.versions.promotionRules') },
          ]}
          values={jsonValues}
          documentKey={documentKey}
          disabled={readOnly}
          onChange={editor.applyPatch}
          onValidityChange={editor.setJsonValidity}
        />
      </div>

      <div className={activeTab === 'governance' ? '' : 'hidden'} role="tabpanel">
        <VersionGovernanceTab
          executionMode={draft.executionMode}
          defaultDecisionMode={draft.defaultDecisionMode}
          autonomyFences={draft.autonomyFences}
          computeClasses={draft.computeClasses}
          archetype={draft.archetype}
          documentKey={documentKey}
          disabled={readOnly}
          onChange={editor.applyPatch}
          onValidityChange={editor.setJsonValidity}
        />
      </div>

      <div className={activeTab === 'prompts' ? '' : 'hidden'} role="tabpanel">
        <VersionPromptsTab
          bindings={editor.promptBindings}
          disabled={readOnly}
          adding={addingBinding}
          error={bindingErrorKey ? t(bindingErrorKey) : null}
          onAdd={(request) => void handleAddBinding(request)}
        />
      </div>
    </main>
  );
};

export default AIStudioVersionEditorPage;
